import { canonicalWorkflowJSON, normalizeWorkflowReference } from './workflowValueNormalization';
export { canonicalWorkflowJSON, normalizeWorkflowReference } from './workflowValueNormalization';
import type {
  WorkflowAccessRule,
  WorkflowApprovalStepDefinition,
  WorkflowTemplate,
  WorkflowTemplateCreateInput,
} from '../model';
import { workflowFieldsAreValid } from '../WorkflowFieldsEditor';
import { resolveWorkflowTemplateIconKey } from '../workflowTemplateIcons';
import {
  normalizeWorkflowApprovalSteps,
  workflowApprovalStageSequenceIsValid,
} from '../workflowApprovalStages';
import { defaultApprovalSteps, defaultFields } from '../fixtures/workflowPageFixtures';

export function workflowTemplateToDraft(template: WorkflowTemplate): WorkflowTemplateCreateInput {
  return {
    displayName: template.displayName,
    category: template.category,
    iconKey: resolveWorkflowTemplateIconKey(template),
    description: template.version.description,
    fields: template.version.fields.map(({ id: _id, ...field }) => ({
      ...field,
      config: field.config ? structuredClone(field.config) : undefined,
      validation: field.validation ? structuredClone(field.validation) : undefined,
    })),
    approvalSteps: normalizeWorkflowApprovalSteps(template.version.approvalSteps.map(({ id: _id, ...step }) => ({
      ...step,
      resolverConfig: step.resolverConfig ? structuredClone(step.resolverConfig) : undefined,
      approvalLabel: step.approvalLabel ? { ...step.approvalLabel } : undefined,
    }))),
    accessRules: template.version.accessRules.map(({ id: _id, ...rule }) => ({ ...rule })),
  };
}

export function workflowTemplateVersionSnapshot(draft: WorkflowTemplateCreateInput) {
  const fields = [...draft.fields]
    .sort((left, right) => left.order - right.order || left.key.localeCompare(right.key))
    .map((field) => ({
      key: field.key,
      order: field.order,
      type: field.type,
      label: field.label.trim(),
      helpText: (field.helpText ?? '').trim(),
      required: Boolean(field.required),
      sensitivity: field.sensitivity || 'INTERNAL',
      searchable: Boolean(field.searchable),
      exportable: Boolean(field.exportable),
      config: canonicalWorkflowJSON(field.config ?? {}),
      validation: canonicalWorkflowJSON(field.validation ?? {}),
    }));
  const approvalSteps = [...draft.approvalSteps]
    .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label))
    .map((step) => {
      const approvalLabelID = normalizeWorkflowReference(step.approvalLabelId);
      return {
        order: step.order,
        stageOrder: step.stageOrder,
        label: step.label.trim(),
        approvalLabel: approvalLabelID
          ? { id: approvalLabelID }
          : { id: '', name: '승인', tone: 'GREEN' },
        resolverType: step.resolverType.trim().toUpperCase(),
        resolverConfig: canonicalWorkflowJSON(step.resolverConfig ?? {}),
        missingPolicy: step.missingPolicy?.trim().toUpperCase() || 'FAIL',
        selfPolicy: step.selfPolicy?.trim().toUpperCase() || 'SKIP',
        duplicatePolicy: step.duplicatePolicy?.trim().toUpperCase() || 'SKIP',
        dueAfterMinutes: step.dueAfterMinutes ?? null,
        requesterEditable: step.requesterEditable,
      };
    });
  const requestedAccessRules = draft.accessRules?.length
    ? draft.accessRules
    : [{ capability: 'REQUEST', principalType: 'ALL_EMPLOYEES', includeDescendants: true }] satisfies WorkflowAccessRule[];
  const accessRules = requestedAccessRules.map((rule) => {
    const capability = rule.capability.trim().toUpperCase();
    const principalType = rule.principalType.trim().toUpperCase();
    return {
      capability,
      principalType,
      principalId: principalType === 'ALL_EMPLOYEES' ? '' : normalizeWorkflowReference(rule.principalId),
      includeDescendants: Boolean(rule.includeDescendants),
    };
  }).sort((left, right) => {
    const leftKey = `${left.capability}:${left.principalType}:${left.principalId}`;
    const rightKey = `${right.capability}:${right.principalType}:${right.principalId}`;
    return leftKey.localeCompare(rightKey);
  });
  return canonicalWorkflowJSON({
    description: (draft.description ?? '').trim(),
    fields,
    approvalSteps,
    accessRules,
  });
}

export function workflowTemplateVersionMatchesDraft(template: WorkflowTemplate, draft: WorkflowTemplateCreateInput): boolean {
  return JSON.stringify(workflowTemplateVersionSnapshot(workflowTemplateToDraft(template)))
    === JSON.stringify(workflowTemplateVersionSnapshot(draft));
}

export function workflowTemplateVersionsEquivalent(left: WorkflowTemplate, right: WorkflowTemplate): boolean {
  return left.version.id === right.version.id
    && left.version.version === right.version.version
    && left.version.status === right.version.status
    && JSON.stringify(workflowTemplateVersionSnapshot(workflowTemplateToDraft(left)))
      === JSON.stringify(workflowTemplateVersionSnapshot(workflowTemplateToDraft(right)));
}

export function workflowTemplateMutationFingerprint(stage: string, target: string, input: unknown): string {
  return `${stage}:${target}:${JSON.stringify(canonicalWorkflowJSON(input))}`;
}

export function emptyTemplateDraft(): WorkflowTemplateCreateInput { return { displayName: '', category: '', iconKey: 'DOCUMENT', description: '', fields: defaultFields(), approvalSteps: defaultApprovalSteps(), accessRules: [{ capability: 'REQUEST', principalType: 'ALL_EMPLOYEES', includeDescendants: true }] }; }
export function templateValidationMessage(value: WorkflowTemplateCreateInput): string {
  if (!value.displayName.trim()) return '양식 이름을 입력하세요.';
  if (value.displayName.trim().length > 120) return '양식 이름은 120자 이내로 입력하세요.';
  if ((value.description ?? '').trim().length > 1000) return '양식 설명은 1,000자 이내로 입력하세요.';
  if (value.fields.length === 0) return '필드를 하나 이상 추가하세요.';
  if (!workflowFieldsAreValid(value.fields)) return '필드 이름·키와 선택 항목을 확인하세요.';
  if (value.fields.some((field) => field.label.trim().length > 120 || (field.helpText ?? '').trim().length > 500)) return '필드 이름은 120자, 도움말은 500자 이내로 입력하세요.';
  if (value.approvalSteps.length === 0) return '기본 결재 단계를 하나 이상 추가하세요.';
  if (value.approvalSteps.some((step) => !step.label.trim())) return '모든 결재 단계의 이름을 입력하세요.';
  if (value.approvalSteps.some((step) => step.dueAfterMinutes !== undefined && (!Number.isInteger(step.dueAfterMinutes) || step.dueAfterMinutes < 1 || step.dueAfterMinutes > 2_147_483_647))) return '결재 처리 기한을 1분 이상으로 확인하세요.';
  if (value.accessRules?.some((rule) => rule.principalType !== 'ALL_EMPLOYEES' && !rule.principalId?.trim())) return '사용 권한의 대상 조직이나 역할을 선택하세요.';
  if (!workflowApprovalStageSequenceIsValid(value.approvalSteps)) return '병렬 승인 단계 구성을 확인하세요.';
  return '';
}
export function newApprovalStep(order: number): WorkflowApprovalStepDefinition { return { order, stageOrder: order, label: `${order}차 승인`, approvalLabel: { name: '승인', tone: 'GREEN' }, resolverType: 'REQUESTER_ORG_LEADER', resolverConfig: { level: order }, missingPolicy: 'FAIL', selfPolicy: 'SKIP', duplicatePolicy: 'SKIP', requesterEditable: true }; }
export function newAccessRule(): WorkflowAccessRule { return { capability: 'REQUEST', principalType: 'ALL_EMPLOYEES', includeDescendants: true }; }
export function updateAccessRule(value: WorkflowTemplateCreateInput, onChange: (value: WorkflowTemplateCreateInput) => void, index: number, patch: Partial<WorkflowAccessRule>) { onChange({ ...value, accessRules: (value.accessRules ?? []).map((rule, current) => current === index ? { ...rule, ...patch } : rule) }); }
export function reorderSteps(steps: WorkflowApprovalStepDefinition[]) { return normalizeWorkflowApprovalSteps(steps); }
export function approvalDescription(step: WorkflowApprovalStepDefinition) {
  if (step.resolverType === 'REQUESTER_ORG_LEADER') return `요청자의 ${Number(step.resolverConfig?.level ?? 1)}차 조직장${step.missingPolicy === 'FALLBACK_ROOT_ORG_LEADER' ? ' · 없으면 CEO' : ''}`;
  return ({ ROOT_ORG_LEADER: '공식 최상위 조직장', FIXED_PERSON: '특정 구성원', FIXED_ROLE: '특정 역할', FORM_PERSON_FIELD: '양식에서 선택한 구성원', SUBJECT_ORG_LEADER: '대상 구성원의 조직장' } as Record<string, string>)[step.resolverType] ?? step.resolverType;
}
export function accessRuleLabel(rule: WorkflowAccessRule) { return `${({ REQUEST: '신청', READ: '열람', MANAGE: '관리' } as Record<string, string>)[rule.capability]} · ${({ ALL_EMPLOYEES: '전체 구성원', ROLE: '역할', SUPERVISORY_ORGANIZATION: '조직' } as Record<string, string>)[rule.principalType]}`; }
